import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
  dayLabels,
  describeCondition,
  describeTrigger,
  isActionId,
  routineTemplates,
  routineVariables,
  statusLabel,
  time,
  type JsonSchema,
  type Routine,
  type RoutineCondition,
  type RoutineInput,
  type RoutineStep,
  type RoutineTrigger,
} from '@elena/shared';
import { Icon } from '../components/Icon';
import { api, ApiError, type ActionMeta } from '../lib/api';
import { session } from '../lib/session';

type Draft = RoutineInput & { id?: string };

const templates = routineTemplates;

const emptyDraft = (): Draft => ({
  name: 'Nova rutina',
  enabled: true,
  stopOnError: false,
  triggers: [{ type: 'voice', phrase: '' }],
  conditions: [],
  steps: [],
});

const toDraft = (r: Routine): Draft => ({
  id: r.id,
  name: r.name,
  enabled: r.enabled,
  stopOnError: r.stopOnError,
  triggers: r.triggers,
  conditions: r.conditions,
  steps: r.steps,
});

export function Routines() {
  const [list, setList] = useState<Routine[] | null>(null);
  const [meta, setMeta] = useState<ActionMeta[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const [r, a] = await Promise.all([api.routines(), api.actions()]);
    setList(r.routines);
    setMeta(a.actions.filter((x) => x.id !== 'routine.run'));
    return r.routines;
  }, []);

  useEffect(() => {
    void reload().then((routines) => {
      if (routines[0] && window.matchMedia('(min-width: 861px)').matches)
        setDraft(toDraft(routines[0]));
    });
  }, [reload]);

  const selected = list?.find((r) => r.id === draft?.id);

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setMessage(null);
    try {
      const { id, ...input } = draft;
      const res = id ? await api.updateRoutine(id, input) : await api.createRoutine(input);
      await reload();
      setDraft(toDraft(res.routine));
      setMessage({ kind: 'ok', text: 'Sačuvano.' });
    } catch (err) {
      setMessage({
        kind: 'error',
        text: err instanceof ApiError ? err.message : 'Nisam uspela da sačuvam.',
      });
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    if (!draft?.id) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await api.runRoutine(draft.id, session.origin());
      setMessage({ kind: res.run.status === 'done' ? 'ok' : 'error', text: res.text });
      await reload();
    } catch (err) {
      setMessage({
        kind: 'error',
        text: err instanceof ApiError ? err.message : 'Rutina nije pokrenuta.',
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!draft?.id || !window.confirm(`Obrisati rutinu „${draft.name}“?`)) return;
    await api.deleteRoutine(draft.id);
    setDraft(null);
    await reload();
  };

  return (
    <div className={`page routines${draft ? ' is-editing' : ''}`}>
      <aside className="routine-list" aria-label="Rutine">
        <header className="page-head">
          <div>
            <h1>Rutine</h1>
            <p className="muted">
              Jedna rečenica, vreme ili događaj na serveru, pa više koraka redom.
            </p>
          </div>
          <button type="button" className="btn primary" onClick={() => setDraft(emptyDraft())}>
            <Icon name="zap" size={16} /> Nova
          </button>
        </header>

        {list === null ? (
          <p className="muted">Učitavam…</p>
        ) : (
          <ul className="list card">
            {list.length === 0 && (
              <li className="muted">Još nema rutina. Kreni od šablona ispod.</li>
            )}
            {list.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  className={`routine-row${draft?.id === r.id ? ' is-active' : ''}`}
                  onClick={() => {
                    setDraft(toDraft(r));
                    setMessage(null);
                  }}
                >
                  <span className={`dot ${r.enabled ? 'on' : ''}`} />
                  <span className="grow">
                    <span className="strong block">{r.name}</span>
                    <span className="muted small">
                      {r.triggers.map(describeTrigger).join(' · ') || 'Samo ručno'}
                    </span>
                  </span>
                  <span className="muted small">{r.steps.length} kor.</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <section className="templates">
          <h2>Šabloni</h2>
          {templates.map((t) => (
            <button
              key={t.label}
              type="button"
              className="template"
              onClick={() => setDraft(structuredClone(t.draft))}
            >
              <span className="strong">{t.label}</span>
              <span className="muted small">{t.hint}</span>
            </button>
          ))}
        </section>
      </aside>

      {draft && (
        <section className="routine-editor" aria-label="Uređivanje rutine">
          <div className="editor-top">
            <button type="button" className="btn editor-back" onClick={() => setDraft(null)}>
              Nazad
            </button>
            <label htmlFor="r-name" className="sr-only">
              Ime rutine
            </label>
            <input
              id="r-name"
              className="field name-field"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              maxLength={60}
            />
            <div className="row gap-8 wrap">
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={draft.enabled}
                  onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
                />
                Uključena
              </label>
              {draft.id && (
                <button type="button" className="btn" onClick={() => void runNow()} disabled={busy}>
                  Probaj sada
                </button>
              )}
              <button
                type="button"
                className="btn primary"
                onClick={() => void save()}
                disabled={busy || draft.steps.length === 0}
              >
                Sačuvaj
              </button>
            </div>
          </div>

          {message && (
            <p className={`notice${message.kind === 'ok' ? ' subtle' : ''}`} role="status">
              {message.text}
            </p>
          )}

          <div className="editor-grid">
            <TriggersEditor
              triggers={draft.triggers}
              onChange={(triggers) => setDraft({ ...draft, triggers })}
            />
            <ConditionsEditor
              conditions={draft.conditions}
              onChange={(conditions) => setDraft({ ...draft, conditions })}
            />
          </div>

          <StepsEditor
            steps={draft.steps}
            meta={meta}
            onChange={(steps) => setDraft({ ...draft, steps })}
          />

          <label className="toggle">
            <input
              type="checkbox"
              checked={draft.stopOnError}
              onChange={(e) => setDraft({ ...draft, stopOnError: e.target.checked })}
            />
            Stani na prvom koraku koji ne uspe
          </label>

          {selected && selected.runs.length > 0 && (
            <section className="card">
              <h2>Poslednja izvršavanja</h2>
              <ul className="list">
                {selected.runs.slice(0, 8).map((run) => (
                  <li key={run.id} className="list-row">
                    <span
                      className={`tag tag-${run.status === 'partial' ? 'pending' : run.status}`}
                    >
                      {run.status === 'partial'
                        ? 'Delimično'
                        : run.status === 'running'
                          ? 'Radi'
                          : statusLabel[run.status]}
                    </span>
                    <span className="grow">{run.trigger}</span>
                    <span className="muted small">{time(run.startedAt)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {draft.id && (
            <button type="button" className="btn danger self-start" onClick={() => void remove()}>
              Obriši rutinu
            </button>
          )}
        </section>
      )}
    </div>
  );
}

// ---------- triggers ----------

const triggerTypes: Array<{ type: RoutineTrigger['type']; label: string }> = [
  { type: 'voice', label: 'Kad kažem' },
  { type: 'schedule', label: 'U zadato vreme' },
  { type: 'server_down', label: 'Kad server padne' },
  { type: 'server_up', label: 'Kad se server podigne' },
  { type: 'player_join', label: 'Kad neko uđe' },
];

const newTrigger = (type: RoutineTrigger['type']): RoutineTrigger => {
  switch (type) {
    case 'voice':
      return { type, phrase: '' };
    case 'schedule':
      return { type, time: '21:00', days: [1, 2, 3, 4, 5] };
    case 'player_join':
      return { type, names: [] };
    default:
      return { type };
  }
};

function TriggersEditor({
  triggers,
  onChange,
}: {
  triggers: RoutineTrigger[];
  onChange: (t: RoutineTrigger[]) => void;
}) {
  const update = (i: number, t: RoutineTrigger) =>
    onChange(triggers.map((x, j) => (j === i ? t : x)));
  return (
    <section className="card">
      <h2>Kada se pokreće</h2>
      {triggers.length === 0 && (
        <p className="muted small">
          Bez okidača rutina radi samo ručno ili kad kažeš Eleni njeno ime.
        </p>
      )}
      {triggers.map((t, i) => (
        <div key={i} className="sub-card">
          <div className="row gap-8">
            <span className="strong grow">
              {triggerTypes.find((x) => x.type === t.type)?.label}
            </span>
            <button
              type="button"
              className="icon-btn small-icon"
              aria-label="Ukloni okidač"
              onClick={() => onChange(triggers.filter((_, j) => j !== i))}
            >
              <Icon name="x" size={16} />
            </button>
          </div>
          {t.type === 'voice' && (
            <input
              className="field"
              aria-label="Rečenica"
              placeholder="npr. spremi igru"
              value={t.phrase}
              onChange={(e) => update(i, { ...t, phrase: e.target.value })}
            />
          )}
          {t.type === 'schedule' && (
            <div className="row gap-8 wrap">
              <input
                type="time"
                className="field time-field"
                aria-label="Vreme"
                value={t.time}
                onChange={(e) => update(i, { ...t, time: e.target.value })}
              />
              <div className="days" role="group" aria-label="Dani">
                {[1, 2, 3, 4, 5, 6, 0].map((d) => {
                  const on = t.days.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      className={`day${on ? ' is-on' : ''}`}
                      aria-pressed={on}
                      onClick={() =>
                        update(i, {
                          ...t,
                          days: on ? t.days.filter((x) => x !== d) : [...t.days, d].sort(),
                        })
                      }
                    >
                      {dayLabels[d]}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {t.type === 'player_join' && (
            <input
              className="field"
              aria-label="Imena igrača"
              placeholder="Imena odvojena zarezom, prazno = bilo ko"
              value={t.names.join(', ')}
              onChange={(e) =>
                update(i, {
                  ...t,
                  names: e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
            />
          )}
          <span className="muted small">{describeTrigger(t)}</span>
        </div>
      ))}
      <AddMenu
        label="Dodaj okidač"
        options={triggerTypes.map((x) => ({ value: x.type, label: x.label }))}
        onPick={(v) => onChange([...triggers, newTrigger(v as RoutineTrigger['type'])])}
      />
    </section>
  );
}

// ---------- conditions ----------

const conditionTypes: Array<{ type: RoutineCondition['type']; label: string }> = [
  { type: 'server_online', label: 'Samo ako server radi' },
  { type: 'pc_connected', label: 'Samo ako je računar povezan' },
  { type: 'time_between', label: 'Samo u određeno doba' },
];

function ConditionsEditor({
  conditions,
  onChange,
}: {
  conditions: RoutineCondition[];
  onChange: (c: RoutineCondition[]) => void;
}) {
  return (
    <section className="card">
      <h2>Samo ako</h2>
      {conditions.length === 0 && (
        <p className="muted small">Bez uslova rutina radi uvek kad se okine.</p>
      )}
      {conditions.map((c, i) => (
        <div key={i} className="sub-card">
          <div className="row gap-8">
            <span className="strong grow">{describeCondition(c)}</span>
            <button
              type="button"
              className="icon-btn small-icon"
              aria-label="Ukloni uslov"
              onClick={() => onChange(conditions.filter((_, j) => j !== i))}
            >
              <Icon name="x" size={16} />
            </button>
          </div>
          {c.type === 'time_between' && (
            <div className="row gap-8">
              <input
                type="time"
                className="field time-field"
                aria-label="Od"
                value={c.from}
                onChange={(e) =>
                  onChange(conditions.map((x, j) => (j === i ? { ...c, from: e.target.value } : x)))
                }
              />
              <span className="muted">do</span>
              <input
                type="time"
                className="field time-field"
                aria-label="Do"
                value={c.to}
                onChange={(e) =>
                  onChange(conditions.map((x, j) => (j === i ? { ...c, to: e.target.value } : x)))
                }
              />
            </div>
          )}
        </div>
      ))}
      <AddMenu
        label="Dodaj uslov"
        options={conditionTypes.map((x) => ({ value: x.type, label: x.label }))}
        onPick={(v) =>
          onChange([
            ...conditions,
            v === 'time_between'
              ? { type: 'time_between', from: '18:00', to: '01:00' }
              : ({ type: v } as RoutineCondition),
          ])
        }
      />
    </section>
  );
}

// ---------- steps ----------

function StepsEditor({
  steps,
  meta,
  onChange,
}: {
  steps: RoutineStep[];
  meta: ActionMeta[];
  onChange: (s: RoutineStep[]) => void;
}) {
  const byId = useMemo(() => Object.fromEntries(meta.map((m) => [m.id, m])), [meta]);
  const update = (i: number, s: RoutineStep) => onChange(steps.map((x, j) => (j === i ? s : x)));
  const move = (i: number, dir: -1 | 1) => {
    const next = [...steps];
    const [item] = next.splice(i, 1);
    if (item) next.splice(i + dir, 0, item);
    onChange(next);
  };

  const options = [
    ...meta.map((m) => ({ value: `action:${m.id}`, label: m.description.replace(/\.$/, '') })),
    { value: 'wait', label: 'Sačekaj nekoliko sekundi' },
    { value: 'say', label: 'Elena nešto kaže' },
  ];

  return (
    <section className="card">
      <h2>Koraci, redom</h2>
      {steps.length === 0 && <p className="muted small">Dodaj bar jedan korak.</p>}
      <ol className="step-list">
        {steps.map((s, i) => (
          <li key={i} className="sub-card step-card">
            <div className="row gap-8">
              <span className="step-num" aria-hidden="true">
                {i + 1}
              </span>
              <span className="strong grow">
                {s.type === 'action'
                  ? (byId[s.action]?.description.replace(/\.$/, '') ?? s.action)
                  : s.type === 'wait'
                    ? 'Sačekaj'
                    : 'Elena kaže'}
              </span>
              <button
                type="button"
                className="icon-btn small-icon"
                aria-label="Pomeri gore"
                disabled={i === 0}
                onClick={() => move(i, -1)}
              >
                <Icon name="send" size={14} />
              </button>
              <button
                type="button"
                className="icon-btn small-icon flip"
                aria-label="Pomeri dole"
                disabled={i === steps.length - 1}
                onClick={() => move(i, 1)}
              >
                <Icon name="send" size={14} />
              </button>
              <button
                type="button"
                className="icon-btn small-icon"
                aria-label="Ukloni korak"
                onClick={() => onChange(steps.filter((_, j) => j !== i))}
              >
                <Icon name="x" size={16} />
              </button>
            </div>
            {s.type === 'wait' && (
              <label className="row gap-8">
                <input
                  type="number"
                  className="field num-field"
                  min={1}
                  max={600}
                  value={s.seconds}
                  onChange={(e) =>
                    update(i, {
                      ...s,
                      seconds: Math.max(1, Math.min(600, Number(e.target.value) || 1)),
                    })
                  }
                />
                <span className="muted">sekundi</span>
              </label>
            )}
            {s.type === 'say' && (
              <input
                className="field"
                aria-label="Šta da kaže"
                value={s.text}
                maxLength={300}
                onChange={(e) => update(i, { ...s, text: e.target.value })}
              />
            )}
            {s.type === 'action' && byId[s.action] && (
              <ParamsForm
                schema={byId[s.action]!.params}
                value={s.params}
                onChange={(params) => update(i, { ...s, params })}
              />
            )}
          </li>
        ))}
      </ol>
      {steps.some((s) => s.type !== 'wait') && (
        <p className="muted small">
          U tekstu možeš da koristiš{' '}
          {routineVariables.map((v, i) => (
            <span key={v.key}>
              {i > 0 && ', '}
              <code title={v.label}>{`{${v.key}}`}</code>
            </span>
          ))}
          .
        </p>
      )}
      <AddMenu
        label="Dodaj korak"
        options={options}
        onPick={(v) => {
          if (v === 'wait') onChange([...steps, { type: 'wait', seconds: 5 }]);
          else if (v === 'say') onChange([...steps, { type: 'say', text: 'Gotovo.' }]);
          else {
            const action = v.slice('action:'.length);
            if (isActionId(action)) onChange([...steps, { type: 'action', action, params: {} }]);
          }
        }}
      />
    </section>
  );
}

/** Generic form for an action's params, generated from its JSON schema. */
function ParamsForm({
  schema,
  value,
  onChange,
}: {
  schema: JsonSchema;
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
}) {
  const base = useId();
  const props = Object.entries(schema.properties ?? {});
  if (props.length === 0) return null;
  return (
    <div className="params">
      {props.map(([key, p]) => {
        const id = `${base}-${key}`;
        const label = p.description ?? key;
        const current = value[key] ?? p.default ?? '';
        if (p.enum) {
          return (
            <label key={key} htmlFor={id} className="param">
              <span className="muted small">{label}</span>
              <select
                id={id}
                className="field"
                value={String(current)}
                onChange={(e) => onChange({ ...value, [key]: e.target.value })}
              >
                <option value="" disabled>
                  Izaberi…
                </option>
                {p.enum.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </label>
          );
        }
        const numeric = p.type === 'integer' || p.type === 'number';
        return (
          <label key={key} htmlFor={id} className="param">
            <span className="muted small">{label}</span>
            <input
              id={id}
              className="field"
              type={numeric ? 'number' : 'text'}
              min={p.minimum}
              max={p.maximum}
              maxLength={p.maxLength}
              value={String(current)}
              onChange={(e) => {
                const raw = e.target.value;
                const next = { ...value };
                if (raw === '') delete next[key];
                else next[key] = numeric ? Number(raw) : raw;
                onChange(next);
              }}
            />
          </label>
        );
      })}
    </div>
  );
}

function AddMenu({
  label,
  options,
  onPick,
}: {
  label: string;
  options: Array<{ value: string; label: string }>;
  onPick: (value: string) => void;
}) {
  return (
    <label className="add-menu">
      <span className="sr-only">{label}</span>
      <select
        className="field"
        value=""
        onChange={(e) => {
          if (e.target.value) onPick(e.target.value);
        }}
      >
        <option value="">+ {label}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
